import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Popover, Tooltip } from "@mui/material";

import { clx } from "../../../utils/Helpers";
import UIAvatar from "../UIAvatar";
import "./_UIAvatarStack.scss";

/**
 * Overlapping avatar stack. Hover an avatar to see that person's name; click the
 * stack to open a popover listing everyone (avatar + name). Falls back to
 * `emptyLabel` when there is no one to show.
 */
const UIAvatarStack = ({
  items,
  max,
  size,
  variant,
  emptyLabel,
  className,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const people = useMemo(
    () =>
      Array.isArray(items)
        ? items.filter((person) => person && (person.name || person.image))
        : [],
    [items]
  );

  if (!people.length) {
    return <span className="ui-avatar-stack__empty">{emptyLabel}</span>;
  }

  const visible = people.slice(0, max);
  const overflow = people.slice(max);
  const open = Boolean(anchorEl);

  const openPopover = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <button
        type="button"
        className={clx("ui-avatar-stack", `ui-avatar-stack--${size}`, className)}
        onClick={openPopover}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {visible.map((person, index) => (
          <Tooltip key={person.id ?? index} title={person.name || ""} arrow>
            <span className="ui-avatar-stack__item">
              <UIAvatar
                size={size}
                variant={variant}
                name={person.name}
                image={person.image}
              />
            </span>
          </Tooltip>
        ))}

        {overflow.length > 0 && (
          <Tooltip
            title={overflow
              .map((person) => person.name)
              .filter(Boolean)
              .join(", ")}
            arrow
          >
            <span
              className={clx(
                "ui-avatar-stack__item",
                "ui-avatar-stack__count",
                `ui-avatar-stack__count--${size}`
              )}
            >
              {`+${overflow.length}`}
            </span>
          </Tooltip>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        classes={{ paper: "ui-avatar-stack__popover" }}
      >
        <ul className="ui-avatar-stack__list">
          {people.map((person, index) => (
            <li className="ui-avatar-stack__list-item" key={person.id ?? index}>
              <UIAvatar
                size="xs"
                variant={variant}
                name={person.name}
                image={person.image}
              />
              <span className="ui-avatar-stack__list-name">
                {person.name || emptyLabel}
              </span>
            </li>
          ))}
        </ul>
      </Popover>
    </>
  );
};

UIAvatarStack.propTypes = {
  /** People to show, in order. `{ id, name, image }`. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      image: PropTypes.string,
    })
  ),
  /** Avatars rendered before collapsing the rest into a `+N` chip. */
  max: PropTypes.number,
  size: PropTypes.oneOf(["xs", "sm"]),
  variant: PropTypes.oneOf(["brand", "muted"]),
  emptyLabel: PropTypes.string,
  className: PropTypes.string,
};

UIAvatarStack.defaultProps = {
  items: [],
  max: 3,
  size: "xs",
  variant: "muted",
  emptyLabel: "-",
  className: "",
};

export default UIAvatarStack;
